/**
 * Memoria del visitante, en el `localStorage` **del origen del cliente**.
 *
 * Safari bloquea el almacenamiento de terceros y Chrome lo particiona por sitio de primer nivel, así
 * que el `localStorage` del iframe no recuerda a nadie de forma fiable. Aquí, además, la clave queda
 * por dominio del cliente: un visitante no se rastrea entre webs distintas.
 */

export interface VisitorRecord {
  /** Identidad del visitante emitida por el servidor. Permite reanudar su conversación. */
  token?: string;

  /**
   * Último color de marca conocido. Permite al lanzador pintar con el color correcto antes de abrir
   * el panel, cuando todavía no se ha hablado con el servidor.
   */
  color?: string;

  /**
   * Último avatar confirmado por el servidor. Una cadena vacía dice que el servidor confirmó que no
   * hay ninguno, y no es lo mismo que no saberlo: entonces manda el del snippet.
   */
  avatar?: string;
}

/** Una clave por publicación, para que dos agentes en la misma web no se pisen. */
function storageKey(publicId: string): string {
  return `atlas.visitor.${publicId}`;
}

/**
 * Copia en memoria por si el almacenamiento no está disponible: navegación privada, cuota agotada o
 * desactivado. El widget funciona igual, simplemente no recuerda nada entre páginas.
 */
const fallback = new Map<string, VisitorRecord>();

export function readVisitor(publicId: string): VisitorRecord {
  try {
    const raw = window.localStorage.getItem(storageKey(publicId));
    if (!raw) return fallback.get(publicId) ?? {};

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};

    const record = parsed as Record<string, unknown>;

    return {
      ...(typeof record['token'] === 'string' ? { token: record['token'] } : {}),
      ...(typeof record['color'] === 'string' ? { color: record['color'] } : {}),
      ...(typeof record['avatar'] === 'string' ? { avatar: record['avatar'] } : {})
    };
  } catch {
    return fallback.get(publicId) ?? {};
  }
}

export function writeVisitor(publicId: string, patch: VisitorRecord): void {
  const merged = { ...readVisitor(publicId), ...patch };

  fallback.set(publicId, merged);

  try {
    window.localStorage.setItem(storageKey(publicId), JSON.stringify(merged));
  } catch {
    // Ya está en la copia en memoria: sirve para el resto de la página.
  }
}
